import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { dateAfterDays, formatDate, formatQty } from "@buildpy/core/lib/format";
import type { HouseItem, HouseSpace } from "@buildpy/core/modules/house/build";
import { customerAsPath, customerProjectPath } from "@buildpy/core/modules/project/customer-visible";
import type { CustomerHouseView } from "@buildpy/core/views/g3b/customer-house";
import { verifyContractShare } from "@/api/actions/contract-share";
import UnusableLink from "@/modules/contract/ui/unusable-link";
import ShareGate from "@/modules/share/ui/share-gate";

export const metadata: Metadata = {
  title: "우리 집 기록",
  robots: { index: false, follow: false },
};

interface Props {
  token: string;
  data: CustomerHouseView;
}

// 보증 기한이 오늘(KST) 이후면 「보증 중」.
export const underWarranty = (item: Pick<HouseItem, "warranty_until">, today: string): boolean =>
  item.warranty_until !== null && item.warranty_until >= today;

const Notice = ({ text }: { text: string }) => (
  <CustomerScreenShell>
    <SectionCard as="div" className="mx-auto w-full max-w-sm p-5 text-center text-sm" padding="none" role="alert">
      {text}
    </SectionCard>
  </CustomerScreenShell>
);

const Header = ({ token }: { token: string }) => (
  <header className="pt-safe sticky top-0 z-10 border-b border-border bg-card">
    <div className="flex items-center gap-x-1 px-2 py-1.5">
      <Button asChild aria-label="공사 첫 화면으로" size="icon" variant="ghost">
        <Link data-testid="customer-house-back" href={customerProjectPath(token)}>
          <ChevronLeft />
        </Link>
      </Button>
      <h1 className="text-base font-semibold">우리 집 기록</h1>
    </div>
  </header>
);

const qtyText = (item: HouseItem): string | null => (item.qty === null ? null : `${formatQty(item.qty)}${item.unit}`);

const ItemCard = ({ token, item, today }: { token: string; item: HouseItem; today: string }) => {
  const on = underWarranty(item, today);
  const qty = qtyText(item);
  return (
    <SectionCard as="li" className="flex flex-col gap-y-1.5 text-sm" data-testid="customer-house-item">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{item.part ?? item.space}</span>
        <Pill tone={on ? "done" : "neutral"}>{on ? "보증 중" : "보증 없음"}</Pill>
      </div>
      <p className="font-semibold">{item.name}</p>
      {qty && <p className="text-xs text-muted-foreground">{qty}</p>}
      <p className="text-xs" data-testid="customer-house-warranty">
        {item.warranty_until ? `보증 ${formatDate(item.warranty_until)} 까지` : "보증 기한이 아직 정해지지 않았어요"}
      </p>
      <div className="flex justify-end">
        <Button asChild size="sm" variant="outline">
          <Link href={customerAsPath(token)}>A/S 접수</Link>
        </Button>
      </div>
    </SectionCard>
  );
};

const SpaceChips = ({ spaces, value, onChange }: { spaces: HouseSpace[]; value: string; onChange: (key: string) => void }) => (
  <div aria-label="공간" className="flex flex-wrap gap-1.5" data-testid="customer-house-spaces" role="radiogroup">
    {spaces.map((s) => (
      <Button
        key={s.key}
        aria-checked={value === s.key}
        role="radio"
        size="chip"
        type="button"
        variant="chip"
        onClick={() => onChange(s.key)}>
        {s.label}
      </Button>
    ))}
  </div>
);

// 고객 우리 집 기록 — 계약 견적 줄을 공간별로 · 줄마다 보증 기한 · 금액은 없다.
const Page = ({ token, data }: Props) => {
  const spaces = data.kind === "ok" ? data.house.spaces.filter((s) => s.items.length > 0) : [];
  const [picked, setPicked] = useState<string | null>(null);
  if (data.kind === "dead") return <UnusableLink />;
  if (data.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate docName="우리 집 기록" orgName={data.orgName} token={token} verify={verifyContractShare} />
      </CustomerScreenShell>
    );
  }
  if (data.kind === "notice") return <Notice text={data.text} />;

  const today = dateAfterDays(0);
  const { house } = data;
  const items = spaces.flatMap((s) => s.items);
  const warrantied = items.filter((i) => underWarranty(i, today)).length;
  const current = spaces.find((s) => s.key === picked) ?? spaces[0] ?? null;
  const completion = house.completion_date ? ` · 완료 ${formatDate(house.completion_date)}` : "";

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <Header token={token} />
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4 px-4 pt-4">
        <SectionCard className="flex flex-col gap-y-3" data-testid="customer-house-summary">
          <div className="flex flex-col gap-y-0.5">
            <p className="text-sm text-muted-foreground">
              {data.orgName}
              {completion}
            </p>
            {data.site_address && <p className="text-base font-semibold">{data.site_address}</p>}
          </div>
          <dl className="grid grid-cols-2 gap-2 text-center">
            <div className="flex flex-col rounded-md bg-muted px-2 py-2">
              <dt className="text-xs text-muted-foreground">기록</dt>
              <dd className="text-base font-semibold tabular-nums">{items.length}개</dd>
            </div>
            <div className="flex flex-col rounded-md bg-muted px-2 py-2">
              <dt className="text-xs text-muted-foreground">보증 중</dt>
              <dd className="text-base font-semibold tabular-nums">{warrantied}개</dd>
            </div>
          </dl>
        </SectionCard>

        {current === null ? (
          <EmptyState as="div" className="text-center text-sm text-muted-foreground">
            아직 기록할 항목이 없어요.
          </EmptyState>
        ) : (
          <>
            <SpaceChips spaces={spaces} value={current.key} onChange={setPicked} />
            <ul className="flex flex-col gap-y-2" data-testid="customer-house-list">
              {current.items.map((item) => (
                <ItemCard key={item.key} item={item} today={today} token={token} />
              ))}
            </ul>
          </>
        )}

        <p className="text-xs text-muted-foreground">
          계약 견적서 항목에서 자동으로 채워졌어요. 보증 기한은 {house.base_date ? `${formatDate(house.base_date)} 기준` : "준공일이 정해지면"} 공종별로 계산해요.
        </p>
      </div>
    </CustomerScreenShell>
  );
};

export default Page;
